import type { Dayjs } from 'dayjs';
import type { ScoutingObservation as ScoutingObservationAPI } from '/@/hooks/scouting-observations';
import type { VisitReportStatus, VisitReportType } from '/@/hooks/visit-reports';
import type { CropMonitoring, CropType } from '/@/types/crop-monitoring';

export type SectionId =
    | 'texts-and-images'
    | 'gdu-evolution'
    | 'precipitations-history'
    | 'temperatures-trend'
    | 'pines-registered-in-fieldview'
    | 'claims'
    | 'applications'
    | 'application-traffic-light';

/** "Textos, fotos o tablas" is the only section that can be added several times to a lote. */
export const TEXTS_SECTION: SectionId = 'texts-and-images';

/** Index in `texts` of the "Textos, fotos o tablas" section at `position` of `sections`. */
export const getTextIndex = (sections: SectionId[], position: number) =>
    sections.slice(0, position).filter((sectionId) => sectionId === TEXTS_SECTION).length;

/** Sections that have an optional "Evolución diaria" table the user can include in the report. */
export const DAILY_EVOLUTION_SECTIONS: SectionId[] = ['temperatures-trend', 'gdu-evolution', 'precipitations-history'];

export interface FieldObservation {
    id?: number;
    field: number;
    fieldName: string;
    campaign: number | null;
    cropMonitoring: CropMonitoring | null;
    plantingDate: string | null;
    productName: string;
    vegetativeStage?: string;
    order: number;
    /** Rich text of each "Textos, fotos o tablas" section, in the order they appear in `sections`. */
    texts: string[];
    scoutingObservations: ScoutingObservation[];
    sections: SectionId[];
    /** Sections (from DAILY_EVOLUTION_SECTIONS) whose daily evolution table is included in the report. */
    dailyEvolutionSections: SectionId[];
    /** Claims of the field's seguimiento selected in "Reclamos". */
    claims: number[];
    /** Applications selected in "Aplicaciones": registered in the app, and synced from FieldView. */
    applicationRegisters: number[];
    applicationReportResults: number[];
    crop: CropType;
}

export interface VisitReportFormData {
    status: VisitReportStatus;
    type: VisitReportType;
    userNode: number;
    /** Visit date; remote reports use the day they are generated. */
    date: Dayjs | null;
    /** Period of the field data; empty means the whole campaign. */
    periodStart: Dayjs | null;
    periodEnd: Dayjs | null;
    participants: string;
    /** Grower name; only for reports on the user's own fields ("Mis lotes"). */
    growerName: string;
    fieldObservations: FieldObservation[];
    observation: string;
}

export interface SectionItem {
    id: SectionId;
    title: string;
    description?: string;
    icon: React.ReactNode;
    order: number;
}

export type ScoutingObservation = ScoutingObservationAPI;

/** Period of the field data as days (YYYY-MM-DD); missing bounds mean the whole campaign. */
export type DataPeriod = { start?: string | null; end?: string | null };

export interface SectionDropData {
    type: 'sidebar-section';
    sectionId: SectionId;
    sectionTitle: string;
    sectionDescription?: string;
}

export interface SectionConfig {
    id: SectionId;
    title: string;
    renderComponent: (props: SectionRenderProps) => React.ReactNode;
}

export interface SectionRenderProps {
    fieldObservation: FieldObservation;
    index: number;
    sectionId: SectionId;
    onRemove: (index: number, sectionId: SectionId) => void;
    onContentChange?: (content: string) => void;
    isDragging: boolean;
    dragHandlers: {
        onDragStart: (e: React.DragEvent) => void;
        onDragOver: (e: React.DragEvent) => void;
        onDrop: (e: React.DragEvent) => void;
        onDragEnd: () => void;
    };
}
